import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results"];

  connect() {
    this._index = [];
    this._selectedIdx = -1;
    this._boundClose = this._onOutsideClick.bind(this);

    // Build index after DOM is fully settled
    requestAnimationFrame(() => {
      this._index = this._buildIndex();
    });

    document.addEventListener("click", this._boundClose);
  }

  disconnect() {
    document.removeEventListener("click", this._boundClose);
  }

  // ── Public Stimulus actions ──────────────────────────────────────────────

  query(event) {
    const q = event.target.value.trim().toLowerCase();
    if (!q) {
      this._hide();
      return;
    }
    const matches = this._index
      .filter(
        (e) =>
          e.text.toLowerCase().includes(q) ||
          e.section.toLowerCase().includes(q)
      )
      .slice(0, 12);
    this._render(matches, q);
  }

  keydown(event) {
    const items = this.resultsTarget.querySelectorAll(
      ".docs-search-result-item"
    );
    if (!items.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this._selectedIdx = Math.min(this._selectedIdx + 1, items.length - 1);
      this._updateSelected(items);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this._selectedIdx = Math.max(this._selectedIdx - 1, 0);
      this._updateSelected(items);
    } else if (event.key === "Enter") {
      if (this._selectedIdx >= 0 && items[this._selectedIdx]) {
        items[this._selectedIdx].click();
      }
    } else if (event.key === "Escape") {
      this._hide();
      this.inputTarget.blur();
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────

  _buildIndex() {
    const entries = [];
    const sidebar = document.querySelector("[data-sidebar-target='nav']");
    if (!sidebar) return entries;

    const sections = sidebar.querySelectorAll("li.docs-sidebar-section");
    sections.forEach((section) => {
      const sectionTitle =
        section
          .querySelector(".docs-sidebar-section-label > .docs-sidebar-label-text")
          ?.textContent?.trim() ?? "";

      // Top-level section heading itself
      if (sectionTitle) {
        const firstLink = section.querySelector("a.docs-sidebar-link");
        if (firstLink) {
          entries.push({
            text: sectionTitle,
            href: firstLink.getAttribute("href") || "",
            section: "Section",
            isSection: true,
          });
        }
      }

      // All links within the section
      section.querySelectorAll("a.docs-sidebar-link").forEach((link) => {
        const text = this._linkText(link);
        const href = link.getAttribute("href") || "";
        if (text && href.includes("#")) {
          entries.push({ text, href, section: sectionTitle, isSection: false });
        }
      });
    });

    return entries;
  }

  // Reads a sidebar link's text. API-reference links are rewritten by
  // SidebarController into <span class="cs-http-badge">POST</span><span
  // class="cs-http-label">Create Wallet</span> with no whitespace between the
  // spans, so textContent alone glues the method to the name ("POSTCreate
  // Wallet"). Join the badge and label with a space when both are present.
  _linkText(link) {
    const badge = link.querySelector(".cs-http-badge");
    const label = link.querySelector(".cs-http-label");
    if (badge && label) {
      return `${badge.textContent.trim()} ${label.textContent.trim()}`.trim();
    }
    return link.textContent.trim();
  }

  _render(matches, q) {
    this._selectedIdx = -1;

    if (!matches.length) {
      this.resultsTarget.innerHTML = `<div class="docs-search-no-results">No results for "<strong>${this._escape(q)}</strong>"</div>`;
      this.resultsTarget.classList.add("open");
      return;
    }

    this.resultsTarget.innerHTML = matches
      .map(
        (m) => `
        <a href="${this._escape(m.href)}" class="docs-search-result-item${m.isSection ? " is-section" : ""}">
          <span class="docs-search-result-title">${this._highlight(m.text, q)}</span>
          <span class="docs-search-result-section">${this._escape(m.section)}</span>
        </a>`
      )
      .join("");

    this.resultsTarget.querySelectorAll(".docs-search-result-item").forEach((item) => {
      item.addEventListener("click", () => {
        this._hide();
        this.inputTarget.value = "";
      });
    });

    this.resultsTarget.classList.add("open");
  }

  _highlight(text, q) {
    const safe = this._escape(text);
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return safe.replace(new RegExp(`(${escaped})`, "gi"), "<mark>$1</mark>");
  }

  _escape(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  _updateSelected(items) {
    items.forEach((item, i) => {
      item.classList.toggle("selected", i === this._selectedIdx);
      if (i === this._selectedIdx) item.scrollIntoView({ block: "nearest" });
    });
  }

  _hide() {
    if (!this.hasResultsTarget) return;
    this.resultsTarget.innerHTML = "";
    this.resultsTarget.classList.remove("open");
    this._selectedIdx = -1;
  }

  _onOutsideClick(e) {
    if (!this.element.contains(e.target)) {
      this._hide();
    }
  }
}
